import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, CheckCircle, AlertTriangle, XCircle, RefreshCw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface SecurityValidationResults {
  validation_timestamp: string;
  validated_by: string;
  rls_status: Record<string, { rls_enabled: boolean; has_policies: boolean }>;
  total_security_policies: number;
  admin_function_working: boolean;
  critical_functions_have_search_path: Record<string, boolean>;
  security_level: 'high' | 'medium' | 'needs_improvement';
}

export const SecurityValidation = () => {
  const [validationResults, setValidationResults] = useState<SecurityValidationResults | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const runValidation = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error: validationError } = await supabase.rpc('validate_security_configuration');
      
      if (validationError) {
        throw validationError;
      }
      
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const results = data as unknown as SecurityValidationResults;
        setValidationResults(results);
        toast({
          title: "Security Validation Complete",
          description: `Security level: ${results.security_level.toUpperCase()}`,
          variant: results.security_level === 'high' ? 'default' : 'destructive'
        });
      } else {
        throw new Error('Invalid validation results format');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to run security validation';
      setError(errorMessage);
      toast({
        title: "Validation Failed",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    runValidation();
  }, []);

  const getSecurityLevelColor = (level: string) => {
    switch (level) {
      case 'high': return 'bg-green-500/10 text-green-500 border-green-500/20';
      case 'medium': return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
      case 'needs_improvement': return 'bg-red-500/10 text-red-500 border-red-500/20';
      default: return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
    }
  };

  const getSecurityIcon = (level: string) => {
    switch (level) {
      case 'high': return <CheckCircle className="h-5 w-5" />;
      case 'medium': return <AlertTriangle className="h-5 w-5" />;
      case 'needs_improvement': return <XCircle className="h-5 w-5" />;
      default: return <Shield className="h-5 w-5" />;
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-6 w-6" />
            Security Configuration Validation
          </CardTitle>
          <CardDescription>
            Comprehensive security assessment of database policies and functions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex justify-between items-center mb-6">
            <Button 
              onClick={runValidation} 
              disabled={loading}
              className="flex items-center gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Validating...' : 'Run Security Validation'}
            </Button>
            
            {validationResults && (
              <Badge className={getSecurityLevelColor(validationResults.security_level)}>
                {getSecurityIcon(validationResults.security_level)}
                Security Level: {validationResults.security_level.toUpperCase()}
              </Badge>
            )}
          </div>

          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {validationResults && (
            <div className="space-y-4">
              {/* RLS Status */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Row Level Security Status</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-3">
                    {validationResults.rls_status && Object.entries(validationResults.rls_status).map(([table, status]) => (
                      <div key={table} className="flex justify-between items-center p-3 border rounded-lg">
                        <span className="font-medium">{table}</span>
                        <div className="flex gap-2">
                          <Badge variant={status.rls_enabled ? "default" : "destructive"}>
                            RLS: {status.rls_enabled ? 'Enabled' : 'Disabled'}
                          </Badge>
                          <Badge variant={status.has_policies ? "default" : "destructive"}>
                            Policies: {status.has_policies ? 'Present' : 'Missing'}
                          </Badge>
                        </div>
                      </div>
                    ))}
                    {!validationResults.rls_status && (
                      <div className="text-center text-gray-500 py-4">
                        No RLS status data available
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Policy Count */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Security Policies</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex justify-between items-center">
                    <span>Total Active Security Policies</span>
                    <Badge className={validationResults.total_security_policies >= 12 ? "bg-green-500/10 text-green-500" : "bg-yellow-500/10 text-yellow-500"}>
                      {validationResults.total_security_policies} policies
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Function Security */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Function Security</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span>Admin Function Status</span>
                      <Badge variant={validationResults.admin_function_working ? "default" : "destructive"}>
                        {validationResults.admin_function_working ? 'Working' : 'Error'}
                      </Badge>
                    </div>
                    
                    {validationResults.critical_functions_have_search_path && Object.entries(validationResults.critical_functions_have_search_path).map(([func, hasProtection]) => (
                      <div key={func} className="flex justify-between items-center">
                        <span className="text-sm">{func}</span>
                        <Badge variant={hasProtection ? "default" : "destructive"}>
                          Search Path: {hasProtection ? 'Protected' : 'Vulnerable'}
                        </Badge>
                      </div>
                    ))}
                    {!validationResults.critical_functions_have_search_path && (
                      <div className="text-center text-gray-500 py-2">
                        No function security data available
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Validation Metadata */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Validation Details</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-2 text-sm">
                    <div className="flex justify-between">
                      <span>Validated At:</span>
                      <span>{new Date(validationResults.validation_timestamp).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Validated By:</span>
                      <span>{validationResults.validated_by || 'System'}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};